import React, { useEffect, useReducer, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import styles from "../Aibox/AiboxForm.module.css"
import { Button } from 'primereact/button';
import Lottie from 'lottie-react';
import fail from '../../../../../../assets/animations/fail.json'
import success from '../../../../../../assets/animations/success.json'
import { dahuaLogin, getDahuaEventHandlers, unlinkDahua, addDahua } from '../../../../../../Api Methods/Api';
import SelectableListRow from '../SelectableListRow';

const TextReducer = (state, action) => {
    if (action.type === 'USER_INPUT') {
        return { value: action.val, isValid: action.val.length > 0 };
    }

    return state;
}

// extraField2 holds the array of event names that was sent as `eventsToHandle` when the
// integration was created/last updated.
const getControlledEvents = (integration) => {
    try {
        const parsed = integration?.extraField2 ? JSON.parse(integration.extraField2) : [];
        const events = Array.isArray(parsed) ? parsed : (parsed?.eventsToHandle ?? []);
        return new Set(events);
    } catch {
        return new Set();
    }
}

// extraField3 holds the array of channel ids that was sent as `channelIds` when the
// integration was created/last updated.
const getControlledChannelIds = (integration) => {
    try {
        const parsed = integration?.extraField3 ? JSON.parse(integration.extraField3) : [];
        const channelIds = Array.isArray(parsed) ? parsed : (parsed?.channelIds ?? []);
        return new Set(channelIds.map(channel => channel?.channel ?? channel));
    } catch {
        return new Set();
    }
}

const DahuaEditForm = ({ integration, apiData, siteUUID, pin, onClose, refreshDevices }) => {
    const dispatch = useDispatch();
    const userID = useSelector(state => state.auth.userID);

    const [existingIP, existingPort] = (integration?.extraField1 ?? '').split(':');
    const controlledEvents = getControlledEvents(integration);
    const controlledChannelIds = getControlledChannelIds(integration);

    const [step, setStep] = useState('form');
    const [failReason, setFailReason] = useState("");

    const [availableEvents, setAvailableEvents] = useState([]);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [availableChannels, setAvailableChannels] = useState([]);
    const [selectedChannels, setSelectedChannels] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [eventsError, setEventsError] = useState("");

    const handleEventSelected = (event) => {
        setSelectedEvents((prevSelectedEvents) => {
            if (prevSelectedEvents.includes(event)) {
                return prevSelectedEvents.filter(
                    (selectedEvent) => selectedEvent !== event
                );
            } else {
                return [...prevSelectedEvents, event];
            }
        });
    };

    const handleChannelSelected = (channel) => {
        setSelectedChannels((prevSelectedChannels) => {
            if (prevSelectedChannels.includes(channel.channel)) {
                return prevSelectedChannels.filter(
                    (selectedChannel) => selectedChannel !== channel.channel
                );
            } else {
                return [...prevSelectedChannels, channel.channel];
            }
        });
    };

    useEffect(() => {
        const fetchAvailableEvents = async () => {
            setEventsLoading(true);
            setEventsError("");

            const loginBody = {
                deviceIp: existingIP,
                port: existingPort,
                username: integration.username,
                password: integration.password,
                userID
            };

            try {
                const loginResponse = await dahuaLogin(apiData, loginBody, dispatch);
                if (loginResponse.success) {
                    setAvailableChannels(loginResponse.channels);
                    setSelectedChannels(loginResponse.channels
                        .filter(channel => controlledChannelIds.has(channel.channel))
                        .map(channel => channel.channel));

                    const eventsResponse = await getDahuaEventHandlers(apiData, dispatch);
                    if (eventsResponse.success) {
                        setAvailableEvents(eventsResponse.data);
                        setSelectedEvents(eventsResponse.data.filter(event => controlledEvents.has(event)));
                    } else {
                        setEventsError("Failed to retrieve Dahua events, please try again");
                    }
                } else {
                    setEventsError("Failed to log in to Dahua to retrieve controlled events");
                }
            } catch {
                setEventsError("Failed to log in to Dahua to retrieve controlled events");
            } finally {
                setEventsLoading(false);
            }
        };

        fetchAvailableEvents();
    }, []);

    const [ipAddressState, dispatchIPAddressState] = useReducer(TextReducer, {
        value: existingIP ?? '',
        isValid: true
    });

    const [portState, dispatchPortState] = useReducer(TextReducer, {
        value: existingPort ?? '',
        isValid: true
    });

    const [usernameState, dispatchUsernameState] = useReducer(TextReducer, {
        value: integration?.username ?? '',
        isValid: null
    });

    const [passwordState, dispatchPasswordState] = useReducer(TextReducer, {
        value: integration?.password ?? '',
        isValid: null
    });

    const submitHandler = async (event) => {
        event.preventDefault();
        setStep('saving');

        // Re-authenticate with the (possibly edited) credentials before touching the existing
        // link, so a bad new IP/username/password doesn't leave the pin unlinked.
        const loginBody = {
            deviceIp: ipAddressState.value,
            port: portState.value,
            username: usernameState.value,
            password: passwordState.value,
            userID
        };

        const loginResponse = await dahuaLogin(apiData, loginBody, dispatch);
        if (!loginResponse.success) {
            setFailReason("Failed to log in to Dahua with the details provided, please check them and try again");
            setStep('fail');
            return;
        }

        const unlinkResponse = await unlinkDahua(apiData, siteUUID, pin, dispatch);
        if (!unlinkResponse.success) {
            setFailReason("Failed to unlink the existing Dahua, please try again");
            setStep('fail');
            return;
        }

        const relinkBody = {
            pinNumber: pin,
            siteUUID,
            userID,
            ipAndPort: `${ipAddressState.value}:${portState.value}`,
            eventsToHandle: selectedEvents,
            channels: selectedChannels
        };

        const relinkResponse = await addDahua(apiData, relinkBody, dispatch);
        if (relinkResponse.success) {
            setStep('success');
        } else {
            setFailReason("The existing Dahua was unlinked but relinking with the new details failed - please add it again manually");
            setStep('fail');
        }
    };

    if (step === 'success') {
        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <h3 style={{ textAlign: "center", color: "#858585" }}>Dahua updated successfully!</h3>
                <Lottie animationData={success} loop={false} style={{ width: 250, height: 250, margin: "0px" }} />
                <Button
                    onClick={() => { refreshDevices?.(); onClose?.(); }}
                    style={{ width: "100%" }}
                    severity='secondary'
                    label='Close'
                    icon="pi pi-times"
                />
            </div>
        );
    }

    if (step === 'fail') {
        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Lottie animationData={fail} loop={false} style={{ width: 150, height: 150, margin: "0px" }} />
                <h3 style={{ textAlign: "center", color: "#6e6e6eff" }}>{failReason}</h3>
                <Button icon="pi pi-times" onClick={() => setStep('form')} label="Try again" severity='secondary' />
            </div>
        );
    }

    return (
        <form onSubmit={submitHandler} className={styles["container1"]}>
            <h3 style={{ color: "#6e6e6e" }}>Edit Dahua integration</h3>
            <br />
            <small style={{ color: "#6e6e6e" }}>Please do not include 'https://' or the port in the IP Address field</small>
            <br />

            <input
                placeholder="IP Address"
                value={ipAddressState.value}
                onChange={(e) => dispatchIPAddressState({ type: 'USER_INPUT', val: e.target.value })}
                type="text"
                className={styles.inputField}
                required
            />

            <input
                placeholder="Port"
                value={portState.value}
                onChange={(e) => dispatchPortState({ type: 'USER_INPUT', val: e.target.value })}
                type="text"
                className={styles.inputField}
                required
            />

            <input
                placeholder="Username"
                value={usernameState.value}
                onChange={(e) => dispatchUsernameState({ type: 'USER_INPUT', val: e.target.value })}
                type="text"
                className={styles.inputField}
                required
            />

            <input
                placeholder="Password"
                value={passwordState.value}
                onChange={(e) => dispatchPasswordState({ type: 'USER_INPUT', val: e.target.value })}
                type="text"
                className={styles.inputField}
                required
            />

            <div style={{ width: "100%", marginTop: "5px", textAlign: "left" }}>
                <h4 style={{ color: "#6e6e6e" }}>Events to control</h4>
                {eventsLoading && <p style={{ color: "#6e6e6e" }}>Logging in to Dahua to retrieve events...</p>}
                {!eventsLoading && eventsError && <p className={styles.errorMessage}>{eventsError}</p>}
                {!eventsLoading && !eventsError && (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                        {availableEvents.map((event, index) => (
                            <SelectableListRow
                                key={index}
                                selected={selectedEvents.includes(event)}
                                label={`${event}`}
                                onClick={() => handleEventSelected(event)}
                            />
                        ))}
                    </div>
                )}
            </div>

            <div style={{ width: "100%", marginTop: "5px", textAlign: "left" }}>
                <h4 style={{ color: "#6e6e6e" }}>Channels to control</h4>
                {eventsLoading && <p style={{ color: "#6e6e6e" }}>Logging in to Dahua to retrieve channels...</p>}
                {!eventsLoading && eventsError && <p className={styles.errorMessage}>{eventsError}</p>}
                {!eventsLoading && !eventsError && (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                        {availableChannels.map((channel, index) => (
                            <SelectableListRow
                                key={index}
                                selected={selectedChannels.includes(channel.channel)}
                                label={channel.name}
                                onClick={() => handleChannelSelected(channel)}
                            />
                        ))}
                    </div>
                )}
            </div>
            <br/>
            <Button
                icon="pi pi-save"
                severity="success"
                type="submit"
                disabled={step === 'saving'}
                label={step === 'saving' ? "Saving..." : "Save"}
            />
        </form>
    );
}

export default DahuaEditForm;
